import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import { Loader2, Printer, ArrowLeft, MessageCircle, Copy } from "lucide-react";
import { toast } from "sonner";

const LEVELS = ["0%", "1%", "2%", "3%", "4%", "5%", ">5%*"];

export default function Statement() {
  const { agentId, token } = useParams();
  const [params] = useSearchParams();
  const monthParam = params.get("month") || new Date().toISOString().slice(0, 7);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [err, setErr] = useState(null);
  const [sharing, setSharing] = useState(false);
  const isPublic = !!token;

  useEffect(() => {
    const url = isPublic ? `/public/statement/${token}`
      : user?.role === "agent" ? `/agent/statement?month=${monthParam}` : `/admin/statements/${agentId}?month=${monthParam}`;
    api.get(url).then((r) => setSt(r.data)).catch((e) => setErr(e.response?.data?.detail || "Not available"));
  }, [agentId, monthParam, user, token, isPublic]);

  const shareWhatsApp = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/admin/statements/${agentId}/share`, { month: monthParam, origin: window.location.origin });
      window.open(data.whatsapp_url, "_blank", "noopener");
      try { await navigator.clipboard.writeText(data.url); toast.success("WhatsApp opened - statement link also copied"); } catch { toast.success("WhatsApp opened"); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create share link");
    } finally {
      setSharing(false);
    }
  };

  if (err) return <div className="p-10 text-center text-muted-foreground">{String(err)}</div>;
  if (!st) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  const month = st.month || monthParam;
  const e = st.earnings;
  const local = (v) => money(v * st.agent.fx_rate, st.agent.currency, st.agent.symbol);
  const label = new Date(`${month}-01T00:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto p-6 sm:p-10 print:p-0">
        <div className="flex items-center justify-between mb-6 print:hidden">
          {isPublic ? <span className="text-xs text-muted-foreground">Shared statement · read-only</span>
            : <Button variant="ghost" onClick={() => navigate(-1)} data-testid="statement-back-button"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>}
          <div className="flex gap-2">
            {!isPublic && user?.role !== "agent" && (
              <Button variant="outline" onClick={shareWhatsApp} disabled={sharing} className="border-[#25D366]/60 text-[#128C7E] hover:bg-[#25D366]/10" data-testid="statement-whatsapp-button">
                {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MessageCircle className="w-4 h-4 mr-1" /> Share on WhatsApp</>}
              </Button>
            )}
            <Button onClick={() => window.print()} data-testid="statement-print-button"><Printer className="w-4 h-4 mr-1" /> Download PDF</Button>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-8 print:border-0" data-testid="statement-sheet">
          <div className="flex items-start justify-between">
            <div><Logo size={30} /><p className="text-xs text-muted-foreground mt-2">{st.company} · Sales Agent Earnings Statement</p></div>
            <div className="text-right">
              <p className="font-display text-2xl font-bold tracking-tight">{label}</p>
              <p className="text-sm">{st.agent.name} · {st.agent.agent_code}</p>
              <Badge variant={st.payout.status === "paid" ? "default" : "secondary"} className="mt-2 capitalize" data-testid="statement-payout-status">{st.payout.status === "paid" ? `Paid ${st.payout.paid_on?.slice(0, 10)}` : `Pay by ${st.payout.due_date}`}</Badge>
            </div>
          </div>

          <h3 className="font-semibold text-sm mt-8 mb-2">Your paid bags this month — by discount level</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left font-medium py-2">Product</th>
                {LEVELS.map((l) => <th key={l} className="text-right font-medium py-2">{l}</th>)}
                <th className="text-right font-medium py-2">Commission</th>
                <th className="text-right font-medium py-2">Sales value</th>
              </tr>
            </thead>
            <tbody>
              {st.matrix.map((m) => (
                <tr key={m.product} className="border-b border-border/40" data-testid="statement-matrix-row">
                  <td className="py-2">{m.product}</td>
                  {m.bags.map((b, i) => <td key={`${m.product}-${LEVELS[i]}`} className="py-2 text-right text-muted-foreground">{b || "—"}</td>)}
                  <td className="py-2 text-right font-medium">{money(m.commission)}</td>
                  <td className="py-2 text-right">{money(m.sales)}</td>
                </tr>
              ))}
              {st.matrix.length === 0 && <tr><td colSpan={9} className="py-4 text-center text-muted-foreground text-xs">No paid bags in this month.</td></tr>}
            </tbody>
          </table>

          <h3 className="font-semibold text-sm mt-8 mb-2">Your earnings</h3>
          <div className="text-sm">
            <Row l="Amount collected ABOVE list price" v={e.above_price_extra} />
            <Row l="Total collected sales this month" v={e.collected_sales} />
            <Row l="Base commission (all bags, all levels)" v={e.base_commission} />
            <Row l={`Accelerator tier (${Math.round(e.accelerator_boost * 100)}%)`} v={e.accelerator_bonus} />
            <Row l={`Above-price bonus (${Math.round(st.policy.above_split * 100)}% of premium)`} v={e.above_price_bonus} />
            <Row l={`New-customer bonus (${e.new_customers_cleared} × ${st.policy.new_customer_bonus} AED)`} v={e.new_customer_bonus} />
            {e.star_bonus > 0 && <Row l="Star Agent bonus" v={e.star_bonus} />}
            {(e.deductions > 0 || e.recovery_from_commission > 0) && <Row l="Less: returns / recoveries" v={-(e.deductions + e.recovery_from_commission)} />}
            <div className="flex justify-between py-2 mt-1 border-t-2 border-foreground font-display font-bold text-base">
              <span>TOTAL MONTHLY EARNINGS</span>
              <span data-testid="statement-total">{money(e.total)}{st.agent.currency !== "AED" && <span className="text-muted-foreground font-normal text-sm"> · {local(e.total)}</span>}</span>
            </div>
          </div>

          <h3 className="font-semibold text-sm mt-8 mb-2">Paid invoices included</h3>
          <table className="w-full text-xs">
            <thead><tr className="uppercase tracking-wider text-muted-foreground border-b border-border"><th className="text-left py-1.5">Invoice</th><th className="text-left py-1.5">Customer</th><th className="text-left py-1.5">Date</th><th className="text-left py-1.5">Paid</th><th className="text-right py-1.5">Value</th><th className="text-right py-1.5">Commission</th></tr></thead>
            <tbody>{st.invoices.map((i) => (
              <tr key={i.invoice_no} className="border-b border-border/40"><td className="py-1.5 font-mono">{i.invoice_no}</td><td className="py-1.5">{i.customer}</td><td className="py-1.5">{i.date}</td><td className="py-1.5">{i.paid_date}</td><td className="py-1.5 text-right">{money(i.sales)}</td><td className="py-1.5 text-right">{money(i.commission)}</td></tr>
            ))}</tbody>
          </table>
          <p className="text-[11px] text-muted-foreground mt-8">* discounts above 5% are manager-approved and may carry no commission. Commission is released only on invoices paid in full. Figures in AED{st.agent.currency !== "AED" && ` (converted at 1 AED = ${st.agent.fx_rate} ${st.agent.currency})`}. Generated {st.generated_at.slice(0, 10)}.</p>
        </div>
      </div>
    </div>
  );
}

function Row({ l, v }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-border/40">
      <span className="text-muted-foreground">{l}</span>
      <span className={v < 0 ? "text-destructive" : ""}>{v < 0 ? "−" : ""}{money(Math.abs(v))}</span>
    </div>
  );
}
